import { useState, useEffect } from 'react';
import { api } from '../lib/api';

interface AdminPageProps {
  onNavigate: (page: string, data?: any) => void;
  user: any;
}

export default function AdminPage({ onNavigate, user }: AdminPageProps) {
  const [activeTab, setActiveTab] = useState<'moderation' | 'members'>('moderation');
  const [stories, setStories] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [unpublishModal, setUnpublishModal] = useState<any>(null);
  const [reason, setReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState('');
  const [actionSuccess, setActionSuccess] = useState('');
  const [members, setMembers] = useState<any[]>([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [membersLoading, setMembersLoading] = useState(false);

  const fetchStories = async (p = page) => {
    setLoading(true);
    try {
      const params: Record<string, string> = { page: String(p) };
      if (search) params.search = search;
      if (statusFilter !== 'all') params.status = statusFilter;
      const data = await api.admin.stories(params);
      setStories(data.stories);
      setTotal(data.total);
      setTotalPages(data.totalPages);
      setPage(data.page);
    } catch (err) {
      console.error('Failed to fetch stories:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchMembers = async () => {
    setMembersLoading(true);
    try {
      const data = await api.admin.newMembers();
      setMembers(data.members);
      setTotalUsers(data.totalUsers);
    } catch (err) {
      console.error('Failed to fetch members:', err);
    } finally {
      setMembersLoading(false);
    }
  };

  useEffect(() => {
    if (user?.role === 'ADMIN') {
      fetchStories(1);
      fetchMembers();
    }
  }, [user, statusFilter]);

  const handleSearch = () => {
    fetchStories(1);
  };

  const handleUnpublish = async () => {
    if (!unpublishModal || !reason.trim()) return;
    setActionLoading(true);
    setActionError('');
    try {
      await api.admin.unpublish(unpublishModal.id, reason);
      setActionSuccess(`"${unpublishModal.title}" has been unpublished and the author has been notified.`);
      setUnpublishModal(null);
      setReason('');
      fetchStories();
      setTimeout(() => setActionSuccess(''), 5000);
    } catch (err: any) {
      setActionError(err.message || 'Failed to unpublish');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRepublish = async (story: any) => {
    setActionLoading(true);
    setActionError('');
    try {
      await api.admin.republish(story.id);
      setActionSuccess(`"${story.title}" has been restored.`);
      fetchStories();
      setTimeout(() => setActionSuccess(''), 5000);
    } catch (err: any) {
      setActionError(err.message || 'Failed to republish');
    } finally {
      setActionLoading(false);
    }
  };

  if (!user || user.role !== 'ADMIN') {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center">
        <h2 className="text-2xl font-bold mb-4">Admin access required</h2>
        <p className="text-gray-400">You do not have permission to view this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Admin Panel</h1>
          <p className="text-gray-400 mt-1">{totalUsers} registered users</p>
        </div>
        <span className="px-3 py-1.5 bg-red-900/30 text-red-400 rounded-lg text-sm font-medium">
          Admin
        </span>
      </div>

      <div className="flex gap-1 mb-6 bg-gray-800/50 rounded-lg p-1">
        {(['moderation', 'members'] as const).map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors capitalize ${
              activeTab === tab ? 'bg-ink-600 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
            }`}
          >
            {tab === 'moderation' ? 'Content Moderation' : 'New Members'}
          </button>
        ))}
      </div>

      {actionSuccess && (
        <div className="mb-6 p-4 bg-emerald-900/30 border border-emerald-800 rounded-xl text-emerald-400">
          {actionSuccess}
        </div>
      )}
      {actionError && (
        <div className="mb-6 p-4 bg-red-900/30 border border-red-800 rounded-xl text-red-400">
          {actionError}
        </div>
      )}

      {activeTab === 'members' && (
        <div>
          {membersLoading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <div className="space-y-2">
              {members.map(m => (
                <div key={m.id} className="bg-gray-900 border border-gray-800 rounded-xl p-4 flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-ink-600/30 flex items-center justify-center text-sm font-bold shrink-0">
                    {m.avatarUrl ? (
                      <img src={m.avatarUrl} alt={m.username} className="w-full h-full rounded-full object-cover" />
                    ) : (
                      m.username[0].toUpperCase()
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => onNavigate('profile', { username: m.username })}
                        className="font-semibold text-ink-400 hover:underline"
                      >
                        {m.username}
                      </button>
                      <span className={`px-1.5 py-0.5 text-xs rounded-full font-medium ${
                        m.role === 'ADMIN' ? 'bg-red-900/30 text-red-400'
                        : m.role === 'AUTHOR' ? 'bg-purple-900/30 text-purple-400'
                        : 'bg-gray-800 text-gray-400'
                      }`}>
                        {m.role}
                      </span>
                      {m.emailVerified && (
                        <span className="text-emerald-400 text-xs">Verified</span>
                      )}
                      <span className="text-gray-500 text-xs">Lv.{m.level}</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {m.email} — joined {new Date(m.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}
                    </div>
                  </div>
                  <div className="flex gap-4 text-xs text-gray-400 shrink-0">
                    <span>{m._count.stories} stories</span>
                    <span>{m._count.reviews} reviews</span>
                    <span>{m._count.follows} follows</span>
                  </div>
                </div>
              ))}
              {members.length === 0 && (
                <div className="text-center py-20 bg-gray-900/50 rounded-2xl border border-gray-800">
                  <h2 className="text-xl font-bold mb-2 text-gray-400">No members yet</h2>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {activeTab === 'moderation' && (<>
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="flex-1 flex gap-2">
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            placeholder="Search by title or author..."
            className="flex-1 bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 focus:border-ink-500 focus:outline-none"
          />
          <button
            onClick={handleSearch}
            className="px-5 py-2.5 bg-ink-600 hover:bg-ink-500 rounded-lg font-medium transition-colors"
          >
            Search
          </button>
        </div>
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none"
        >
          <option value="all">All Statuses</option>
          <option value="ONGOING">Ongoing</option>
          <option value="COMPLETED">Completed</option>
          <option value="HIATUS">Hiatus</option>
          <option value="UNPUBLISHED">Unpublished</option>
        </select>
      </div>

      <div className="text-sm text-gray-400 mb-4">{total} stories found</div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <div className="w-10 h-10 border-3 border-ink-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : stories.length === 0 ? (
        <div className="text-center py-20 bg-gray-900/50 rounded-2xl border border-gray-800">
          <h2 className="text-xl font-bold mb-2 text-gray-400">No stories found</h2>
          <p className="text-gray-500">Try a different search or filter.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {stories.map(story => (
            <div key={story.id} className="bg-gray-900 border border-gray-800 rounded-xl p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-lg font-bold truncate">{story.title}</h3>
                    <span className={`px-2 py-0.5 text-xs rounded-full font-medium shrink-0 ${
                      story.status === 'ONGOING' ? 'bg-emerald-900/30 text-emerald-400'
                      : story.status === 'COMPLETED' ? 'bg-blue-900/30 text-blue-400'
                      : story.status === 'UNPUBLISHED' ? 'bg-red-900/30 text-red-400'
                      : 'bg-amber-900/30 text-amber-400'
                    }`}>
                      {story.status}
                    </span>
                    {story.isMature && (
                      <span className="px-2 py-0.5 text-xs rounded-full font-medium bg-red-900/20 text-red-300 shrink-0">
                        Mature
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-400">
                    <span>by <strong className="text-gray-300">{story.author?.username}</strong></span>
                    <span>{story.genre}</span>
                    <span>{story._count?.chapters || 0} chapters</span>
                    <span>{story._count?.follows || 0} followers</span>
                    <span>{story._count?.reviews || 0} reviews</span>
                    <span>{story.totalViews} views</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Created {new Date(story.createdAt).toLocaleDateString()} | Updated {new Date(story.updatedAt).toLocaleDateString()}
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    onClick={() => onNavigate('story', { id: story.id, slug: story.slug })}
                    className="px-3 py-1.5 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors"
                  >
                    View
                  </button>
                  {story.status === 'UNPUBLISHED' ? (
                    <button
                      onClick={() => handleRepublish(story)}
                      disabled={actionLoading}
                      className="px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 rounded-lg text-sm transition-colors disabled:opacity-50"
                    >
                      Republish
                    </button>
                  ) : (
                    <button
                      onClick={() => { setUnpublishModal(story); setReason(''); setActionError(''); }}
                      className="px-3 py-1.5 bg-red-700 hover:bg-red-600 rounded-lg text-sm transition-colors"
                    >
                      Unpublish
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => fetchStories(page - 1)}
            disabled={page <= 1}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm disabled:opacity-30 transition-colors"
          >
            Previous
          </button>
          <span className="text-sm text-gray-400">Page {page} of {totalPages}</span>
          <button
            onClick={() => fetchStories(page + 1)}
            disabled={page >= totalPages}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm disabled:opacity-30 transition-colors"
          >
            Next
          </button>
        </div>
      )}

      </>)}

      {unpublishModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 max-w-lg w-full">
            <h3 className="text-xl font-bold mb-2">Unpublish Story</h3>
            <p className="text-gray-400 mb-4">
              You are about to unpublish <strong className="text-white">"{unpublishModal.title}"</strong> by{' '}
              <strong className="text-white">{unpublishModal.author?.username}</strong>.
              This will hide the story from all public views.
            </p>
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Reason (sent to the author)
            </label>
            <textarea
              value={reason}
              onChange={e => setReason(e.target.value)}
              placeholder="Explain why this story is being unpublished..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 h-32 resize-none focus:border-red-500 focus:outline-none"
            />
            {actionError && (
              <p className="text-red-400 text-sm mt-2">{actionError}</p>
            )}
            <div className="flex gap-3 justify-end mt-4">
              <button
                onClick={() => { setUnpublishModal(null); setReason(''); setActionError(''); }}
                className="px-5 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleUnpublish}
                disabled={!reason.trim() || actionLoading}
                className="px-5 py-2 bg-red-600 hover:bg-red-500 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                {actionLoading ? 'Unpublishing...' : 'Unpublish Story'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
